import type {
  AppNewsDoc,
  CommentaryCommentDoc,
  SolrClientInterface,
} from "@rsc/vv-solr-client";
import { useQuery, type UseQueryReturnType } from "@tanstack/vue-query";
import {
  makeResultAsyncSerializable,
  type SerializableResult,
} from "~/errors/util";
import type { LogFunction } from "~/logging/log";

/**
 * Convenience wrapper for `useQuery` and `SolrClient.getNode`.
 *
 * @param client The Solr client to use.
 * @param nid The ID of the node to fetch (reactive dependency).
 * @param languageCode The language in which to load the node (reactive dependency).
 * @param fields The fields to include in the results.
 * @param indexId The Solr index ID, which corresponds to the content type.
 * @param logServerSideError A callback to log errors when running on the server.
 */
export function useNode<PickFields extends Array<keyof CommentaryCommentDoc>>(
  client: SolrClientInterface,
  nid: Ref<number>,
  languageCode: Ref<string>,
  fields: PickFields,
  indexId: "commentary_comments",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<{
    doc: Pick<CommentaryCommentDoc, PickFields[number]> | null;
    languages: string[];
  }>,
  never
>;
export function useNode<PickFields extends Array<keyof AppNewsDoc>>(
  client: SolrClientInterface,
  nid: Ref<number>,
  languageCode: Ref<string>,
  fields: PickFields,
  indexId: "app_news",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<{
    doc: Pick<AppNewsDoc, PickFields[number]> | null;
    languages: string[];
  }>,
  never
>;
export function useNode<
  DocType extends CommentaryCommentDoc | AppNewsDoc,
  PickFields extends Array<keyof DocType>,
>(
  client: SolrClientInterface,
  nid: Ref<number>,
  languageCode: Ref<string>,
  fields: PickFields,
  indexId: "commentary_comments" | "app_news",
  logServerSideError: LogFunction,
): UseQueryReturnType<
  SerializableResult<{
    doc: Pick<DocType, PickFields[number]> | null;
    languages: string[];
  }>,
  never
> {
  // Workaround for https://www.phind.com/search?cache=coihygy9tz0s1iv3labolkji
  const _fields = fields as any[];

  return useQuery({
    queryKey: ["node", nid, languageCode, _fields, indexId],
    queryFn: () =>
      makeResultAsyncSerializable(
        () => client.getNode(nid.value, languageCode.value, _fields, indexId),
        logServerSideError,
      ),
  });
}
